import { router } from '@inertiajs/react';
import { useState } from 'react';
import { ChevronRight } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import sessionStorage from '../../Shared/sessionStorage';
import { Charity, Event, Pagination, ProjectType, QueryParams } from '../../Shared/types';
import DateRange from '../../Shared/UI/DateRange';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { FadeIn } from '../../Shared/UI/FadeIn';
import { DonationBox, Icon, Medal, Megaphone } from '../../Shared/UI/Icon';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import { Pager } from '../../Shared/UI/Pager';
import { Radio } from '../../Shared/UI/Radio';
import { RadioBox } from '../../Shared/UI/RadioBox';
import RadioLabel from '../../Shared/UI/RadioLabel';
import { SearchFilterInput } from '../../Shared/UI/SearchFilterInput';
import { useCharityStyle } from '../../Shared/useCharityStyle';
import { useRouter } from '../router';
import { Button } from '../UI/Button';

export interface SelectProjectFormProps {
  charity: Charity;
  events: Pagination<Event>;
  projectType?: ProjectType | null;
  params: QueryParams;
}

const EVENT_NOT_FOUND = 'event_not_found';

export default function SelectProjectForm({
  charity,
  events,
  projectType,
  params,
}: SelectProjectFormProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();

  useCharityStyle(charity);

  const storageKey = `select_project_form:${charity.id}`;

  const [storedDefaultValues] = useState<{
    project_type: ProjectType | null;
    event_id: string | null;
  }>(() => {
    // Retrieve the stored form state from session storage and remove it.
    const storedValue = sessionStorage.getItem(storageKey);
    sessionStorage.removeItem(storageKey);

    if (storedValue) {
      return JSON.parse(storedValue);
    }

    return {
      project_type: projectType,
      event_id: null,
    };
  });

  const form = useForm({
    defaultValues: storedDefaultValues,
  });

  const { handleSubmit, watch, formState: { errors, defaultValues } } = form;

  const projectTypeValue = watch('project_type');
  const showEvents = projectTypeValue === ProjectType.Event;

  const submit = (data: typeof defaultValues = {}) => {
    // Store the form values in session storage so that if the user navigates
    // back in the next step, the current form state is restored.
    sessionStorage.setItem(storageKey, JSON.stringify(data));

    if (data.project_type === ProjectType.Fundraising) {
      // Redirect to default fundraising project.
      router.visit(routes.create_campaign_page(charity.id, ProjectType.Fundraising, { source: params.source }));
    } else if (data.project_type === ProjectType.Event) {
      if (data.event_id === EVENT_NOT_FOUND) {
        // Redirect to default fundraising project.
        router.visit(routes.create_campaign_page(charity.id, ProjectType.Fundraising, { source: params.source }));
      } else if (data.event_id) {
        // Redirect to event project.
        router.visit(routes.create_campaign_page(charity.id, ProjectType.Event, { source: params.source, eventId: data.event_id }));
      }
    } else if (data.project_type === ProjectType.DonationBox) {
      router.visit(routes.create_campaign_page(charity.id, ProjectType.DonationBox, { source: params.source }));
    }
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-8 sm:space-y-10">
      <div className="space-y-6">
        <h4 className="font-semibold text-slate-500">
          {t('frontend:campaign_type')}
        </h4>

        {!projectType && (
          <InputGroup>
            <InputLabel htmlFor="select_project_type" required valid={!errors.project_type}>
              {t('frontend:project_type_label')}
            </InputLabel>
            <div className="space-y-2">
              <RadioLabel className="relative p-3 overflow-hidden bg-white border rounded-md border-slate-250 hover:border-slate-300">
                <Radio
                  {...form.register('project_type', { required: true })}
                  value={ProjectType.Fundraising}
                  aria-invalid={!!errors.project_type}
                  id="select_project_type"
                />
                <div className="pr-[72px]">
                  <div className="font-semibold">
                    {t('frontend:fundraising_campaign_label')}
                  </div>
                  <div className="text-slate-500">
                    {t('frontend:fundraising_campaign_description')}
                  </div>
                </div>
                <div className="rounded-l-full w-[100px] h-[160px] bg-project/5 text-project flex items-center justify-center absolute -right-6 pr-5 top-1/2 -mt-[80px] text-2xl">
                  <Icon>
                    <Megaphone strokeWidth={1.5} />
                  </Icon>
                </div>
              </RadioLabel>
              <RadioLabel className="relative p-3 mt-2 overflow-hidden bg-white border rounded-md border-slate-250 hover:border-slate-300">
                <Radio
                  {...form.register('project_type', { required: true })}
                  value={ProjectType.Event}
                  aria-invalid={!!errors.project_type}
                />
                <div className="pr-[72px]">
                  <div className="font-semibold">
                    {t('frontend:event_campaign_label')}
                  </div>
                  <div className="text-slate-500">
                    {t('frontend:event_campaign_description')}
                  </div>
                </div>
                <div className="rounded-l-full w-[100px] h-[160px] bg-project/5 text-project flex items-center justify-center absolute -right-6 pr-5 top-1/2 -mt-[80px] text-2xl">
                  <Icon>
                    <Medal strokeWidth={1.5} />
                  </Icon>
                </div>
              </RadioLabel>
              <RadioLabel className="relative p-3 mt-2 overflow-hidden bg-white border rounded-md border-slate-250 hover:border-slate-300">
                <Radio
                  {...form.register('project_type', { required: true })}
                  value={ProjectType.DonationBox}
                  aria-invalid={!!errors.project_type}
                />
                <div className="pr-[72px]">
                  <div className="font-semibold">
                    {t('frontend:donation_box_campaign_label')}
                  </div>
                  <div className="text-slate-500">
                    {t('frontend:donation_box_campaign_description')}
                  </div>
                </div>
                <div className="rounded-l-full w-[100px] h-[160px] bg-project/5 text-project flex items-center justify-center absolute -right-6 pr-5 top-1/2 -mt-[80px] text-2xl">
                  <Icon>
                    <DonationBox strokeWidth={1.5} />
                  </Icon>
                </div>
              </RadioLabel>
            </div>
            <ErrorMessage attribute={t('frontend:project_type_label')} error={errors.project_type} />
          </InputGroup>
        )}

        <FadeIn
          when={showEvents}
          render={() => (
            <InputGroup>
              <InputLabel htmlFor="select_project_event_0" valid={!errors.event_id} required>
                {t('frontend:event')}
              </InputLabel>
              <div className="space-y-2">
                <SearchFilterInput
                  id="search"
                  name="events_search"
                  placeholder={t('frontend:find_your_event')}
                />
                {events.data.map((event, index) => (
                  <RadioBox size="lg" key={event.id}>
                    <Radio
                      {...form.register('event_id', { required: true })}
                      value={event.id}
                      aria-invalid={!!errors.event_id}
                      id={`select_project_event_${index}`}
                      className="mt-[3px] mr-2"
                    />
                    <div className="flex justify-between items-center w-full">
                      <div className="mr-2">
                        <div className="font-semibold">
                          {event.title}
                        </div>
                        <div className="text-slate-500">
                          <DateRange start={event.start_date} end={event.end_date} />
                        </div>
                      </div>
                      {event.logo_url && (
                        <div className="w-10 h-10 rounded-full overflow-hidden relative">
                          <div className="absolute inset-0 rounded-full border border-black/5" />
                          <img src={event.logo_url} alt={event.title} className="block object-contain w-full h-full" />
                        </div>
                      )}
                    </div>
                  </RadioBox>
                ))}
                {events.data.length === 0 && (
                  <div className="rounded-md bg-slate-50 p-3 text-slate-500">
                    {t('frontend:no_results')}
                  </div>
                )}
                <RadioBox size="lg">
                  <Radio
                    {...form.register('event_id', { required: true })}
                    aria-invalid={!!errors.event_id}
                    value={EVENT_NOT_FOUND}
                    id={`select_project_event_${events.data.length}`}
                    className="mt-1 mr-2"
                  />
                  <div>
                    {t('frontend:other_event_label')}
                  </div>
                </RadioBox>
                {events.meta.last_page > 1 && (
                  <Pager data={events} className="!mt-4" />
                )}
              </div>
              <ErrorMessage attribute={t('frontend:event')} error={errors.event_id} />
            </InputGroup>
          )}
        />
      </div>

      <Button type="submit" size="lg" className="w-full">
        {t('frontend:continue')}
        <Icon className="ml-2">
          <ChevronRight />
        </Icon>
      </Button>
    </form>
  );
}
